import React, { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FileText, ShieldCheck, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api from "@/utils/api";

export default function AVVSection() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchStatus = async () => {
    try {
      const { data } = await api.get("/legal/avv-status");
      setStatus(data);
    } catch (e) {
      // Silent — user may be advisor or not authenticated in unusual flows
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  const handleAccept = async () => {
    setSaving(true);
    try {
      await api.post("/legal/accept-avv");
      toast.success("AVV akzeptiert – vielen Dank!");
      await fetchStatus();
    } catch (e) {
      toast.error("AVV konnte nicht gespeichert werden. Bitte erneut versuchen.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;

  const accepted = status?.accepted;

  return (
    <Card className="rounded-xl border-blue-200" data-testid="avv-section">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-blue-700">
          <ShieldCheck className="h-5 w-5" />
          Auftragsverarbeitungsvertrag (Art. 28 DSGVO)
        </CardTitle>
        <CardDescription>
          Als Verantwortlicher für die Daten deiner Kunden/Lieferanten brauchst du mit ivoliq eine
          AVV. Einmalige Akzeptanz genügt – du kannst sie jederzeit als PDF herunterladen.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {accepted ? (
          <div className="flex flex-col gap-2">
            <div className="text-sm text-emerald-700 font-medium flex items-center gap-2">
              <ShieldCheck className="h-4 w-4" />
              AVV akzeptiert am {new Date(status.accepted_at).toLocaleString("de-DE")}
              {" "}
              (Version {status.accepted_version})
            </div>
            <div className="flex flex-wrap gap-2">
              <a
                href="/avv"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
                data-testid="avv-view-link"
              >
                <ExternalLink className="h-4 w-4" /> Volltext anzeigen
              </a>
              <a
                href="/api/legal/avv-pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
                data-testid="avv-download-link"
              >
                <FileText className="h-4 w-4" /> PDF herunterladen
              </a>
            </div>
            {status.current_version && status.accepted_version !== status.current_version && (
              <div className="text-sm text-orange-600">
                Eine neue Version ({status.current_version}) ist verfügbar. Bitte erneut bestätigen.
                <Button
                  onClick={handleAccept}
                  disabled={saving}
                  size="sm"
                  className="ml-2"
                  data-testid="avv-reaccept-btn"
                >
                  {saving ? "Speichern…" : "Neue Version akzeptieren"}
                </Button>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <div className="text-sm text-gray-700">
              Bitte lies die{" "}
              <a
                href="/avv"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline"
              >
                AVV im Volltext
              </a>{" "}
              (inkl. TOMs und Sub-Processor-Liste) und bestätige sie hier.
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={handleAccept}
                disabled={saving}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="avv-accept-btn"
              >
                {saving ? "Speichern…" : "AVV akzeptieren"}
              </Button>
              <a
                href="/api/legal/avv-pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
                data-testid="avv-preview-pdf"
              >
                <FileText className="h-4 w-4" /> Als PDF ansehen
              </a>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
